import AngleSmallDownSvg from "@icons/angle-small-down.svg?react";
import BoxesSvg from "@icons/boxes.svg?react";
import GarageOpenSvg from "@icons/garage-open.svg?react";
import Gears from "@icons/gears.svg?url";
import PencilSvg from "@icons/pencil.svg?react";
import PlusSvg from "@icons/plus.svg?react";
import { t } from "@lingui/core/macro";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Plural, Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { Failed, Info } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import {
  trackNewStorageClick,
  trackStorageClick,
} from "@/common/components/mixpanel/mixpanel-tracking";
import {
  SearchField,
  useSearchFieldParams,
} from "@/common/components/search-field/search-field";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spinner } from "@/common/components/spinner/spinner";
import { Currency, useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { useIsMobile } from "@/common/hooks/use-media-query";
import { useScrollbarToggle } from "@/common/hooks/use-scrollbar-toggle";
import { currencyFormatter } from "@/common/services/formatter";
import { sum } from "@/common/utils/array";

import { TwoColumnLayout } from "../layout/layout";
import { Storage as TStorage, storagesQuery } from "./api";
import { CommodityCard } from "./components/commodity-card/commodity-card";
import { path as managementPath } from "./storages.management";

const path = "storages";

const Storages = () => {
  const { _ } = useLingui();
  const { id: companyId } = useRequiredErpCompany();
  const { offerId } = useParams<"offerId">();
  const [CurrencySwitch, currency] = useCurrency();
  const [search] = useSearchFieldParams();
  // @ts-expect-error discrepancy between CF API and IDSign API
  const storages = useQuery(storagesQuery({ companyId, currency, search }));
  const location = useLocation();
  const isRightSideOpen =
    location.pathname.includes("management") ||
    location.pathname.includes("offers");
  useScrollbarToggle(isRightSideOpen);

  let jsx: React.ReactNode = <ManagementLink />;

  if (storages.status === "pending") {
    jsx = (
      <div className="flex basis-full items-center justify-center">
        <Spinner />
      </div>
    );
  } else if (storages.status === "error") {
    jsx = <Failed error={storages.error} />;
  } else {
    if (!storages.data) {
      jsx = (
        <>
          <Info>
            <Trans>Ve vaší společnosti zatím neexistují žádné sklady.</Trans>
          </Info>
          <ManagementLink />
        </>
      );
    } else {
      jsx = (
        <>
          <SearchField placeholder={t`Hledat sklad nebo komoditu`} />
          <div className="flex justify-end">
            <Link
              className="flex items-center gap-2"
              onMouseDown={trackNewStorageClick}
              to="management/new"
            >
              <Trans>Nový sklad</Trans>
              <PlusSvg />
            </Link>
          </div>
          <ul className="flex flex-col gap-y-6">
            {storages.data.map((storage) => (
              <Storage key={storage.id} storage={storage}>
                <ul className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 2xl:grid-cols-3">
                  {storage.offers.map((offer) => (
                    <li
                      className={
                        offerId === offer.id
                          ? "[&_article]:bg-can-silver-gray"
                          : ""
                      }
                      key={offer.id}
                    >
                      <SearchParamsLink
                        onMouseDown={() => {
                          trackStorageClick({ name: storage.name });
                        }}
                        to={`${storage.id}/offers/${offer.id}`}
                      >
                        <CommodityCard>
                          <CommodityCard.Title
                            crop={{
                              harvestYear: offer.harvestYear,
                              mark: offer.crop.mark,
                              name: offer.crop.name,
                            }}
                          />
                          <CommodityCard.Price
                            currency={currency}
                            pricePerTon={offer.lastPrice}
                            tons={offer.amount}
                          />
                          <CommodityCard.Stored tons={offer.amount} />
                          <CommodityCard.ProgressBar
                            values={{
                              freeAmount: offer.amount,
                              orderAmount: offer.orderAmount,
                              totalAmount: offer.totalAmount,
                            }}
                          />
                          <CommodityCard.Available
                            available={offer.amount}
                            order={offer.orderAmount}
                            tons={offer.totalAmount}
                          />
                        </CommodityCard>
                      </SearchParamsLink>
                    </li>
                  ))}
                </ul>
                <Overview
                  currency={currency}
                  price={storage.offers
                    .filter((o) => o.lastPrice)
                    .map((o) => o.amount * o.lastPrice!)
                    .reduce(sum, 0)}
                  tons={storage.offers.map((o) => o.amount).reduce(sum, 0)}
                />
                <Actions storageId={storage.id} />
              </Storage>
            ))}
          </ul>
          <ManagementLink />
        </>
      );
    }
  }

  return (
    <TwoColumnLayout
      left={{
        content: (
          <>
            <Metadata title={_(msg`Sklady`)} />
            {jsx}
          </>
        ),
        header: (
          <>
            <h1 className="text-2xl font-bold text-can-forest-teal">
              <Trans>Sklady</Trans>
            </h1>
            {CurrencySwitch}
          </>
        ),
      }}
      right={isRightSideOpen}
    />
  );
};

const Actions = ({ storageId }: { storageId: string }) => {
  return (
    <div className="mt-6 flex justify-between">
      <SearchParamsLink
        className="flex items-center gap-2"
        // TODO create a new route
        to={`management/${storageId}/crops/new`}
      >
        <PlusSvg />
        <Trans>Přidat komoditu</Trans>
      </SearchParamsLink>
      <SearchParamsLink
        className="flex items-center gap-2"
        to={`management/${storageId}`}
      >
        <PencilSvg />
        <Trans>Upravit sklad</Trans>
      </SearchParamsLink>
    </div>
  );
};

const Overview = ({
  price,
  tons,
  currency,
}: {
  price: number;
  tons: number;
  currency: Currency;
}) => {
  return (
    <p
      className="mt-4 flex gap-2 rounded-2xl bg-can-silver-gray px-4 py-3 text-xs"
      role="status"
    >
      <BoxesSvg />
      <Trans>Skladem:</Trans>{" "}
      <Plural
        _0="# tun"
        few="# tuny"
        many="# tun"
        one="# tuna"
        other="# tun"
        value={tons}
      />{" "}
      |{" "}
      <Trans>Hodnota (nyní): {currencyFormatter(currency).format(price)}</Trans>
    </p>
  );
};

const ManagementLink = () => (
  <SearchParamsLink
    className="mb-8 mr-6 mt-auto rounded-2xl border border-can-forest-teal px-4 py-2 text-center text-can-forest-teal"
    style={{
      // eslint-disable-next-line lingui/no-unlocalized-strings
      background: `url(${Gears}) left 16px center no-repeat`,
    }}
    to={managementPath}
  >
    <Trans>Spravovat sklady</Trans>
  </SearchParamsLink>
);

const Storage = ({
  children,
  storage,
}: React.PropsWithChildren<{
  storage: TStorage;
}>) => {
  const isMobile = useIsMobile();

  return (
    <li className="last-of-type:mb-8" key={storage.id}>
      <details
        className="group"
        name={storage.id + "-" + storage.name}
        open={!isMobile}
      >
        <summary className="flex list-none flex-col">
          <h3 className="flex items-center gap-x-2 font-bold text-can-forest-teal [&_path]:fill-[currentColor]">
            <GarageOpenSvg className="h-4 w-4" />
            {storage.mark ? (
              <mark className="text-can-forest-teal">{storage.name}</mark>
            ) : (
              storage.name
            )}
            <AngleSmallDownSvg className="ml-auto rotate-180 group-open:rotate-0" />
          </h3>
        </summary>
        {children}
      </details>
    </li>
  );
};

export { path, Storages };
